import React from 'react';
import { CheckCircle, Award as AwardIcon } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { aboutData, alignmentData } from '../mock/landingData';
import { useTranslation } from 'react-i18next';

const About = () => {
  const { t } = useTranslation();

  return (
    <section id="about" className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4">
        {/* About Francisco */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
          {/* Image */}
          <div className="order-2 lg:order-1">
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={aboutData.image}
                  alt="Francisco Rodrigues Neto, conhecido como Chico da Inclusão, sorrindo em foto profissional vestindo terno escuro e camisa xadrez azul, segurando seu livro sobre direitos e garantias das pessoas com deficiências"
                  className="w-full h-[500px] object-cover object-center"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-teal-600 text-white p-6 rounded-xl shadow-xl max-w-xs">
                <AwardIcon className="mb-2" size={32} />
                <p className="font-bold text-lg">{t('about.award')}</p>
                <p className="text-sm">{t('about.awardText')}</p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="order-1 lg:order-2 space-y-6">
            <div>
              <span className="bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold inline-block mb-4">
                {t('about.badge')}
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-2">
                {t('about.name')}
              </h2>
              <p className="text-xl text-teal-600 font-semibold mb-4">
                {t('about.nickname')}
              </p>
              <p className="text-lg text-gray-700 mb-4">{t('about.title')}</p>
            </div>

            <p className="text-gray-600 leading-relaxed text-lg">
              {t('about.bio')}
            </p>

            {/* Credentials */}
            <div className="space-y-3 pt-4">
              {t('about.credentials', { returnObjects: true }).map((credential, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <CheckCircle className="text-teal-600 flex-shrink-0 mt-1" size={20} />
                  <p className="text-gray-700">{credential}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ESG and ODS Alignment */}
        <Card className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-teal-50">
          <CardContent className="p-10">
            <div className="text-center mb-10">
              <h3 className="text-3xl font-bold text-gray-900 mb-4">
                {t('about.alignmentTitle')}
              </h3>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                {t('about.alignmentDescription')}
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-xl p-6 text-center shadow-md hover:shadow-lg transition-shadow duration-300">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white text-2xl font-bold">8</span>
                </div>
                <p className="font-semibold text-gray-900">ODS 8</p>
                <p className="text-sm text-gray-600 mt-2">{t('about.ods.8')}</p>
              </div>

              <div className="bg-white rounded-xl p-6 text-center shadow-md hover:shadow-lg transition-shadow duration-300">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white text-2xl font-bold">10</span>
                </div>
                <p className="font-semibold text-gray-900">ODS 10</p>
                <p className="text-sm text-gray-600 mt-2">{t('about.ods.10')}</p>
              </div>

              <div className="bg-white rounded-xl p-6 text-center shadow-md hover:shadow-lg transition-shadow duration-300">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white text-2xl font-bold">16</span>
                </div>
                <p className="font-semibold text-gray-900">ODS 16</p>
                <p className="text-sm text-gray-600 mt-2">{t('about.ods.16')}</p>
              </div>
            </div>

            <div className="mt-10 text-center">
              <p className="text-gray-600 mb-4">
                {t('about.frameworks')}
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <span className="bg-white px-6 py-3 rounded-lg font-semibold text-gray-900 shadow-sm">
                  ESG
                </span>
                <span className="bg-white px-6 py-3 rounded-lg font-semibold text-gray-900 shadow-sm">
                  ODS/ONU
                </span>
                <span className="bg-white px-6 py-3 rounded-lg font-semibold text-gray-900 shadow-sm">
                  LBI
                </span>
                <span className="bg-white px-6 py-3 rounded-lg font-semibold text-gray-900 shadow-sm">
                  Lei de Cotas
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default About;
